<script>
    import { onMount } from "svelte";

    import { fly } from "svelte/transition";

    import { isMobile, user } from "../../stores";

    import {
        SearchInput,
        SvelteTable,
        PageLoading,
        ResetFilterLink,
        DefaultDoctorFilter,
        DoctorMultiSearchSelect,
    } from "../../Components/Common/index.svelte";

    import { apiMethods } from "../../Services/index.svelte";

    import {
        replaceURL,
        getFormatDateAndTime,
        showError,
        decodeSearchParams,
        encodeSearchParams,
        syncDefaultDoctorFilter,
        getDaysDifferFrom,
        viewLog,
        goToURL,
        getTodayWithTime,
        getFormatDateAndTimeWithSeconds,
    } from "../../Utils/utilFunctions.svelte";

    import TableFooter from "../../Components/Layout/Table/TableFooter.svelte";
    import { DefaultLogsTraceBackDays } from "../../Utils/utilConstants.svelte";
    import { LogTypes } from "../../Utils/utilEnums.svelte";
    import SearchDateRangeInput from "../../Components/Common/SearchDateRangeInput.svelte";

    export let params = {};

    let isGettingEclipseLogs = false,
        returnEclipseLogsDataArr = [],
        filters = {},
        page = 0,
        pageSize = 25,
        count,
        showFilters = false;

    let selectedDoctors = [];

    const LIMIT_TEXT_LENGTH = 200;

    const columns = [
        {
            key: "createdDate",
            title: "Time",
            headerClass: "text-left pl-3 cursor-default w-[15%] rounded-tl-xl",
            class: "px-3 text-left cursor-pointer w-[15%]",
            value: (v) => getFormatDateAndTimeWithSeconds(v.createdDate),
        },
        {
            key: "actionTypeName",
            title: "Action",
            headerClass: "text-left pl-3 cursor-default w-[10%]",
            class: "px-3 text-left cursor-pointer w-[10%]",
            value: (v) => _getActionName(v.actionType),
        },
        {
            key: "accountName",
            title: "Doctor",
            headerClass: "text-left pl-3 cursor-default w-[16%]",
            class: "px-3 text-left cursor-pointer w-[16%]",
            renderValue: (v) => _getTextWithWrap(v.accountName),
            csvContent: (v) => _getTextWithWrap(v.accountName, true),
        },
        {
            key: "encounterId",
            title: "Encounter ID",
            headerClass: "text-left pl-3 cursor-default w-[10%]",
            class: "px-3 text-left cursor-pointer w-[10%]",
            renderValue: (v) => _getEncounterID(v.encounterId),
            csvContent: (v) => _getEncounterID(v.encounterId, true),
        },
        {
            key: "transactionId",
            title: "Transaction ID",
            headerClass: "text-left pl-3 cursor-default w-[25%]",
            class: "px-3 text-left cursor-pointer w-[25%]",
            renderValue: (v) => _getTextWithWrap(v.transactionId),
            csvContent: (v) => _getTextWithWrap(v.transactionId, true),
        },
        {
            key: "description",
            title: "Description",
            headerClass: "text-left pl-3 cursor-default w-[25%] rounded-tr-xl",
            class: "px-3 text-left cursor-pointer w-[25%]",
            renderValue: (v) => _getTextWithTrim(v.description),
            csvContent: (v) => _getTextWithTrim(v.description, true),
        },
        // {
        //     key: "request",
        //     title: "Request",
        //     headerClass: "text-left pl-3 cursor-default w-[40%] rounded-tr-xl",
        //     class: "px-3 text-left cursor-pointer w-[40%]",
        //     renderValue: (v) => _getTextWithTrim(v.request),
        //     csvContent: (v) => _getTextWithTrim(v.request, true),
        // },
        // {
        //     key: "response",
        //     title: "Response",
        //     headerClass:
        //         "text-left pl-3 cursor-default w-[20%] rounded-tr-xl",
        //     class: "px-3 cursor-pointer w-[20%]",
        //     renderValue: (v) => _getContentWithTooltips(v.response),
        // },
    ];

    onMount(async () => {
        if (params.query) await updateSearchParamsByQuery(params.query);

        filters = await syncDefaultDoctorFilter(filters, "doctorId");

        if (!filters.dateFrom) {
            filters.dateFrom = getDaysDifferFrom(
                -1 * DefaultLogsTraceBackDays,
                new Date(),
            );
            updateURL();
        }

        loadEclipseLogs(page * pageSize, pageSize, _getSearchParams());
    });

    const updateSearchParamsByQuery = async (query) => {
        const arr = decodeSearchParams(query).split("&");

        if (arr.length) {
            for (let i = 0; i < arr.length; i++) {
                if (arr[i].includes("page="))
                    page = Number(arr[i].split("=")[1]);

                if (arr[i].includes("size="))
                    pageSize = Number(arr[i].split("=")[1]);

                if (arr[i].includes("doctorId=")) {
                    if (isNaN(arr[i].split("=")[1])) {
                        filters.doctorId = arr[i].split("=")[1];
                        selectedDoctors = filters.doctorId
                            .split(",")
                            .map((id) => Number(id));
                    } else {
                        filters.doctorId = Number(arr[i].split("=")[1]);
                        selectedDoctors = [Number(filters.doctorId)];
                    }
                }

                if (arr[i].includes("encounterid="))
                    filters.patientEncounter = arr[i].split("=")[1];

                if (arr[i].includes("fromDate="))
                    filters.dateFrom = arr[i].split("=")[1];

                if (arr[i].includes("toDate="))
                    filters.dateTo = arr[i].split("=")[1];

                if (arr[i].includes("search="))
                    filters.search = arr[i].split("=")[1];
            }

            updateURL();
        }
    };

    const loadEclipseLogs = async (start, limit, params) => {
        isGettingEclipseLogs = true;

        await apiMethods.getMethod
            .getEclipseLogs(start, limit, params)
            .then((res) => _handleEclipseLogs(res));

        isGettingEclipseLogs = false;
    };

    const handleFilterSelect = () => {
        page = 0;

        updateURL();

        loadEclipseLogs(0, pageSize, _getSearchParams());
    };

    const handleClickCell = (
        { target: { className, classList } },
        rowData,
        key,
    ) => {
        if (key == "encounterId" && rowData.encounterId)
            goToURL(`/PatientEncounter/view/${rowData.encounterId}/tab=logs`);
        else if (rowData.id) viewLog(LogTypes.Eclipse, rowData.id);
    };

    const handleClickCol = ({ target: { className } }, colData, key) => {};

    const onPageChange = (e) => {
        page = Number(e.detail.page);

        updateURL();

        loadEclipseLogs(
            Number(e.detail.firstDataIndex),
            pageSize,
            _getSearchParams(),
        );
    };

    const onPageSizeChange = (newPageSize) => {
        pageSize = newPageSize;
        page = 0;

        updateURL();

        loadEclipseLogs(0, newPageSize, _getSearchParams());
    };

    const updateURL = () => {
        replaceURL(
            `/EclipseLogs/page=${page}&size=${pageSize}${_getParamsString()}`,
        );
    };

    const handleSelectDoctor = () => {
        filters.doctorId = selectedDoctors.join(",");

        handleFilterSelect();
    };

    const clearFilterDoctor = () => {
        selectedDoctors = [];

        delete filters.doctorId;

        handleFilterSelect();
    };

    // HELPER FUNCTIONS
    const _handleEclipseLogs = async (res) => {
        if (res.error) showError(res.error);
        else {
            returnEclipseLogsDataArr = [...res.results];

            count = res.total;
        }
    };

    const _getSearchParams = () => {
        let params = {};

        if (filters.doctorId) params["filter[doctorId]"] = filters.doctorId;

        if (filters.patientEncounter)
            params["filter[encounterid]"] = filters.patientEncounter;

        if (filters.dateFrom) params["filter[dateFrom]"] = filters.dateFrom;

        if (filters.dateTo) params["filter[dateTo]"] = filters.dateTo;

        if (filters.search) params.search = filters.search;

        return params;
    };

    const _getParamsString = () => {
        let str = "";
        const params = _getSearchParams();

        if (params) {
            if (params["filter[doctorId]"])
                str += `&doctorId=${params["filter[doctorId]"]}`;

            if (params["filter[encounterid]"])
                str += `&encounterid=${params["filter[encounterid]"]}`;

            if (params["filter[dateFrom]"])
                str += `&fromDate=${params["filter[dateFrom]"]}`;

            if (params["filter[dateTo]"])
                str += `&toDate=${params["filter[dateTo]"]}`;

            if (params.search) str += `&search=${params.search}`;
        }

        return encodeSearchParams(str);
    };

    const _getActionName = (actionType) => {
        switch (actionType) {
            case 0:
                return "POST";
            case 1:
                return "GET";
            case 3:
                return "PUT";
            case 4:
                return "DELETE";
            default:
                return "";
        }
    };

    const _getTextWithWrap = (text, isCsvContent = false) => {
        return isCsvContent
            ? `${text}`
            : `
                <div class="w-full break-all">
                    ${text ? text : "-"}
                </div>
                `;
    };

    const _getEncounterID = (encounterId, isCsvContent = false) =>
        isCsvContent
            ? `${encounterId}`
            : `<div class="cursor-pointer hover:underline">${encounterId}</div>`;

    const _getTextWithTrim = (text, isCsvContent = false) => {
        if (_getTextWithTrim) return text;

        const displayText =
            text.length <= LIMIT_TEXT_LENGTH
                ? text
                : text.slice(0, LIMIT_TEXT_LENGTH) + "...";

        return `
                <div class="w-full break-all">
                    ${displayText}
                </div>
                `;
    };

    const toggleFilters = () => {
        showFilters = !showFilters;
    };

    const filterTransition = (node, args) => ($isMobile ? fly(node, args) : "");
</script>

<div class="flex flex-col flex-1 gap-y-4">
    {#if $isMobile}
        <button
            class="btn btn-accent btn-sm w-28 flex flex-row gap-x-3"
            on:click={toggleFilters}
        >
            Filters
            <i
                style="font-size:20px"
                class={showFilters ? "fa arrow-up" : "fa arrow-down"}
                >&#xf104;
            </i>
        </button>
    {/if}

    {#if showFilters || !$isMobile}
        <div
            class="flex flex-wrap gap-4 items-end"
            in:filterTransition={{ duration: 200, y: -100 }}
        >
            <DefaultDoctorFilter
                bind:filter={filters}
                filterPropertyName={"doctorId"}
                onChangeFunc={() => {
                    selectedDoctors = filters["doctorId"].split(",");
                    handleFilterSelect();
                }}
            />

            <DoctorMultiSearchSelect
                bind:searchResult={selectedDoctors}
                onChangeFunc={handleSelectDoctor}
                inputClass="w-32"
                zIndex="3"
            />

            <SearchInput
                bind:value={filters.patientEncounter}
                title="Encounter ID"
                type="number"
                inputFunc={handleFilterSelect}
                isDisable={isGettingEclipseLogs}
            />

            <SearchInput
                bind:value={filters.search}
                title="Search"
                inputFunc={handleFilterSelect}
                isDisable={isGettingEclipseLogs}
            />

            {#if !$user.settings?.disableDateRangeInput}
                <SearchDateRangeInput
                    bind:startDate={filters.dateFrom}
                    bind:endDate={filters.dateTo}
                    title="Date Range"
                    inputClass="borderB w-[272px]"
                    on:change={handleFilterSelect}
                    showClearButton
                    disable={isGettingEclipseLogs}
                    showScrollYearOptions
                    maxYear={new Date(`${getTodayWithTime()}:00`)}
                />
            {:else}
                <SearchInput
                    bind:value={filters.dateFrom}
                    title="Date from"
                    type="date"
                    inputClass="borderB"
                    inputFunc={handleFilterSelect}
                    isDisable={isGettingEclipseLogs}
                    maxDate={filters.dateTo ?? undefined}
                    showScrollYearOptions
                    maxYear={new Date(`${getTodayWithTime()}:00`)}
                />

                <SearchInput
                    bind:value={filters.dateTo}
                    title="Date to"
                    type="date"
                    inputClass="borderB"
                    inputFunc={handleFilterSelect}
                    isDisable={isGettingEclipseLogs}
                    minDate={filters.dateFrom ?? undefined}
                    showScrollYearOptions
                    maxYear={new Date(`${getTodayWithTime()}:00`)}
                />
            {/if}

            <ResetFilterLink
                bind:filter={filters}
                onResetFilter={clearFilterDoctor}
            />
        </div>
    {/if}

    <div class="relative flex-1">
        {#if isGettingEclipseLogs}
            <PageLoading />
        {:else}
            <div class="overflow-y-auto absolute inset-0">
                <SvelteTable
                    {columns}
                    rows={returnEclipseLogsDataArr}
                    clickCellFunc={({ detail: { event, row, key } }) => {
                        handleClickCell(event, row, key);
                    }}
                    clickColFunc={({ detail: { event, row, key } }) => {
                        handleClickCol(event, row, key);
                    }}
                />
            </div>
        {/if}
    </div>

    <TableFooter
        {page}
        {pageSize}
        {count}
        tableData={returnEclipseLogsDataArr}
        {onPageChange}
        {onPageSizeChange}
        csvConfig={{
            columns,
            data: returnEclipseLogsDataArr,
            fileName: "Eclipse Logs",
            allDataApi: async () =>
                await apiMethods.getMethod.getEclipseLogs(
                    0,
                    999999,
                    _getSearchParams(),
                ),
        }}
    />
</div>
